import { useState, useEffect, useRef } from 'react'
import { Search, UserX, Trash2, Plus, Shield, AlertTriangle, Users } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface BlacklistEntry {
  id: number
  customer_nif: string
  customer_id: number | null
  customer_email: string | null
  customer_name: string | null
  customer_type: string | null
  reason: string | null
  created_by: string | null
  created_at: string | null
}

interface CustomerSearchResult {
  codcli: number | null
  name: string
  email: string
  nif: string
  customer_type: string
  already_blacklisted: boolean
}

export default function ReturnsBlacklistTab() {
  const [entries, setEntries] = useState<BlacklistEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [searchFilter, setSearchFilter] = useState('')
  const toast = useToast()

  // Add customer state
  const [showAddForm, setShowAddForm] = useState(false)
  const [customerQuery, setCustomerQuery] = useState('')
  const [searchResults, setSearchResults] = useState<CustomerSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [addReason, setAddReason] = useState('')
  const [adding, setAdding] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    loadBlacklist()
  }, [])

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    if (customerQuery.length < 2) {
      setSearchResults([])
      return
    }
    searchTimeout.current = setTimeout(() => {
      searchCustomers(customerQuery)
    }, 400)
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current)
    }
  }, [customerQuery])

  const loadBlacklist = async () => {
    setLoading(true)
    try {
      const response = await api.get('/returns/admin/blacklist', {
        params: searchFilter ? { search: searchFilter } : {}
      })
      setEntries(response.data.entries)
    } catch (error) {
      console.error('Error loading blacklist:', error)
    } finally {
      setLoading(false)
    }
  }

  const searchCustomers = async (q: string) => {
    setSearching(true)
    try {
      const response = await api.get('/returns/admin/blacklist/search-customer', {
        params: { q }
      })
      setSearchResults(response.data.customers)
    } catch (error) {
      console.error('Error searching customers:', error)
    } finally {
      setSearching(false)
    }
  }

  const handleAdd = async (customer: CustomerSearchResult) => {
    if (!customer.nif) {
      toast.error('Sin NIF', 'Este cliente no tiene NIF/CIF registrado en Factusol')
      return
    }
    setAdding(customer.nif)
    try {
      await api.post('/returns/admin/blacklist', {
        customer_nif: customer.nif,
        customer_email: customer.email || null,
        customer_name: customer.name || null,
        customer_type: customer.customer_type || null,
        reason: addReason || null
      })
      toast.success('Cliente excluido', `${customer.name} (${customer.nif}) ha sido añadido a la lista`)
      setCustomerQuery('')
      setSearchResults([])
      setAddReason('')
      loadBlacklist()
    } catch (error: any) {
      toast.error('Error', error?.response?.data?.detail || 'Error al añadir cliente')
    } finally {
      setAdding(null)
    }
  }

  const handleRemove = async (entry: BlacklistEntry) => {
    setRemoving(entry.customer_nif)
    try {
      await api.delete(`/returns/admin/blacklist/${encodeURIComponent(entry.customer_nif)}`)
      toast.success('Cliente restaurado', `${entry.customer_name || entry.customer_nif} ha sido eliminado de la lista`)
      loadBlacklist()
    } catch (error: any) {
      toast.error('Error', error?.response?.data?.detail || 'Error al eliminar')
    } finally {
      setRemoving(null)
    }
  }

  const getTypeBadge = (type: string | null) => {
    switch (type) {
      case 'PVD':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">PVD</span>
      case 'VIP':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">VIP</span>
      case 'PVP':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">PVP</span>
      default:
        return null
    }
  }

  const filteredEntries = searchFilter
    ? entries.filter(e =>
        (e.customer_nif || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
        (e.customer_name || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
        (e.customer_email || '').toLowerCase().includes(searchFilter.toLowerCase())
      )
    : entries

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Exclusiones de recogida
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Clientes a los que no se ofrece el servicio de recogida con nuestro transporte. Solo podran enviar por su cuenta.
        </p>
      </div>

      {/* Info box */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl p-4 mb-6">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-300">
            <p className="font-medium mb-1">Como funciona</p>
            <ul className="space-y-1 text-amber-700 dark:text-amber-400">
              <li>La exclusion se identifica por <strong>NIF/CIF</strong>, tanto para pedidos online como facturas de Factusol.</li>
              <li>Los clientes en esta lista <strong>no podran usar nuestro servicio de recogida</strong> (ni gratis ni de pago).</li>
              <li>Solo tendran la opcion de enviar el paquete por su propia cuenta.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add customer section */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 mb-6">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-750 rounded-xl transition-colors"
        >
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-indigo-600" />
            <span className="font-medium text-slate-900 dark:text-white">Añadir cliente a la lista</span>
          </div>
          <svg className={`w-5 h-5 text-slate-400 transition-transform ${showAddForm ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAddForm && (
          <div className="px-4 pb-4 space-y-4 border-t border-slate-100 dark:border-slate-700 pt-4">
            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Motivo de exclusion (opcional)
              </label>
              <input
                type="text"
                value={addReason}
                onChange={(e) => setAddReason(e.target.value)}
                placeholder="Ej: Devoluciones abusivas, productos manipulados..."
                className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                         bg-white dark:bg-slate-900 text-slate-900 dark:text-white
                         placeholder:text-slate-400"
              />
            </div>

            {/* Search customer in Factusol */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Buscar cliente en Factusol
              </label>
              <p className="text-xs text-slate-400 mb-2">
                Busca por NIF, nombre, email o telefono
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  placeholder="Buscar por NIF, nombre, email..."
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-white
                           placeholder:text-slate-400"
                />
                {searching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Search results */}
            {searchResults.length > 0 && (
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {searchResults.map((customer, idx) => (
                    <div
                      key={`${customer.nif || ''}-${customer.codcli || idx}`}
                      className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700 last:border-0
                               hover:bg-slate-50 dark:hover:bg-slate-750"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {customer.name || 'Sin nombre'}
                          </span>
                          {getTypeBadge(customer.customer_type)}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs font-mono font-medium text-slate-700 dark:text-slate-300">
                            {customer.nif || 'Sin NIF'}
                          </span>
                          {customer.email && (
                            <span className="text-xs text-slate-400">{customer.email}</span>
                          )}
                        </div>
                      </div>
                      <div className="ml-3 flex-shrink-0">
                        {customer.already_blacklisted ? (
                          <span className="text-xs text-slate-400 italic">Ya excluido</span>
                        ) : !customer.nif ? (
                          <span className="text-xs text-red-400 italic">Sin NIF</span>
                        ) : (
                          <button
                            onClick={() => handleAdd(customer)}
                            disabled={adding === customer.nif}
                            className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200
                                     rounded-lg hover:bg-red-100 disabled:opacity-50
                                     dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/40
                                     transition-colors"
                          >
                            {adding === customer.nif ? (
                              <div className="w-3 h-3 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                            ) : (
                              <span className="flex items-center gap-1">
                                <UserX className="w-3 h-3" />
                                Excluir
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {customerQuery.length >= 2 && !searching && searchResults.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-2">No se encontraron clientes en Factusol</p>
            )}
          </div>
        )}
      </div>

      {/* Current list */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            <h3 className="font-medium text-slate-900 dark:text-white">
              Clientes excluidos
              {entries.length > 0 && (
                <span className="ml-2 text-sm font-normal text-slate-500">({entries.length})</span>
              )}
            </h3>
          </div>
          {entries.length > 5 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Filtrar..."
                className="pl-9 pr-4 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700
                         bg-white dark:bg-slate-900 text-slate-900 dark:text-white w-56"
              />
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Shield className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400 font-medium">No hay clientes excluidos</p>
            <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
              Todos los clientes pueden usar el servicio de recogida
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {filteredEntries.map((entry) => (
              <div
                key={entry.customer_nif}
                className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-bold text-slate-900 dark:text-white">
                      {entry.customer_nif}
                    </span>
                    {getTypeBadge(entry.customer_type)}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-slate-700 dark:text-slate-300">{entry.customer_name || 'Sin nombre'}</span>
                    <span className="text-xs text-slate-400">{entry.customer_email || ''}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {entry.reason && (
                      <span className="text-xs text-red-500 dark:text-red-400 italic">
                        {entry.reason}
                      </span>
                    )}
                    <span className="text-xs text-slate-400">
                      {entry.created_by || 'admin'} - {entry.created_at ? new Date(entry.created_at).toLocaleDateString('es-ES', {
                        day: '2-digit', month: '2-digit', year: 'numeric'
                      }) : 'N/A'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(entry)}
                  disabled={removing === entry.customer_nif}
                  className="ml-3 p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20
                           rounded-lg transition-colors disabled:opacity-50"
                  title="Eliminar de la lista"
                >
                  {removing === entry.customer_nif ? (
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-red-600 rounded-full animate-spin"></div>
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
